import { format, isValid, parse, parseISO } from 'date-fns';

/** `YYYY-MM-DD` in the user's local calendar (not UTC). */
export function formatIsoLocalDate(value: Date): string {
  const day = String(value.getDate()).padStart(2, '0');
  const month = String(value.getMonth() + 1).padStart(2, '0');
  return `${value.getFullYear()}-${month}-${day}`;
}

/**
 * Get date range for "Today" (same local calendar day for from and to).
 * See `.cursor/rules/calendar-dates-local-timezone.mdc`.
 */
export const getTodayRange = () => {
  const today = formatIsoLocalDate(new Date());
  return { dateFrom: today, dateTo: today };
};

/**
 * Get date range for "This Month" (local calendar).
 */
export const getThisMonthRange = () => {
  const now = new Date();
  const dateFrom = new Date(now.getFullYear(), now.getMonth(), 1);
  const dateTo = new Date(now.getFullYear(), now.getMonth() + 1, 0);

  return {
    dateFrom: formatIsoLocalDate(dateFrom),
    dateTo: formatIsoLocalDate(dateTo),
  };
};

/**
 * Get date range for the current local calendar quarter (Q1–Q4).
 */
export const getThisQuarterRange = () => {
  const now = new Date();
  const year = now.getFullYear();
  const startMonth = Math.floor(now.getMonth() / 3) * 3;
  const dateFrom = new Date(year, startMonth, 1);
  const dateTo = new Date(year, startMonth + 3, 0);

  return {
    dateFrom: formatIsoLocalDate(dateFrom),
    dateTo: formatIsoLocalDate(dateTo),
  };
};

/**
 * Get date range for the current local calendar week (Monday through Sunday).
 */
export const getThisWeekRange = () => {
  const now = new Date();
  const dayOfWeek = now.getDay();
  const daysFromMonday = (dayOfWeek + 6) % 7;
  const monday = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate() - daysFromMonday,
  );
  const sunday = new Date(
    monday.getFullYear(),
    monday.getMonth(),
    monday.getDate() + 6,
  );

  return {
    dateFrom: formatIsoLocalDate(monday),
    dateTo: formatIsoLocalDate(sunday),
  };
};

/**
 * Get date range for "Last Month" (local calendar).
 */
export const getLastMonthRange = () => {
  const now = new Date();
  const dateFrom = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const dateTo = new Date(now.getFullYear(), now.getMonth(), 0);

  return {
    dateFrom: formatIsoLocalDate(dateFrom),
    dateTo: formatIsoLocalDate(dateTo),
  };
};

/**
 * Get date range for trailing 1 year ending today (local calendar).
 */
export const getOneYearRange = () => {
  const now = new Date();
  const dateTo = formatIsoLocalDate(now);
  const dateFrom = formatIsoLocalDate(
    new Date(now.getFullYear() - 1, now.getMonth(), now.getDate()),
  );

  return {
    dateFrom,
    dateTo,
  };
};

/** Common date formats to try when parseISO fails (e.g. mm/dd/yyyy, dd/mm/yyyy) */
const FALLBACK_FORMATS = [
  'yyyy-MM-dd',
  'MMMM d, yyyy',
  'MMM d, yyyy',
  'MM/dd/yyyy',
  'dd/MM/yyyy',
  'M/d/yyyy',
  'd/M/yyyy',
] as const;

/**
 * Parse a date string with automatic format detection using date-fns.
 * Tries parseISO first (ISO 8601: "2026-03-08", "2026-03-08T00:00:00.000Z"),
 * then falls back to common formats (yyyy-MM-dd, MM/dd/yyyy, dd/MM/yyyy, etc).
 */
export const parseFlexibleDate = (dateStr?: string): Date | undefined => {
  if (!dateStr || typeof dateStr !== 'string') return undefined;
  const trimmed = dateStr.trim();
  if (!trimmed) return undefined;

  const parsedISO = parseISO(trimmed);
  if (isValid(parsedISO)) return parsedISO;

  const ref = new Date();
  for (const fmt of FALLBACK_FORMATS) {
    try {
      const parsed = parse(trimmed, fmt, ref);
      if (isValid(parsed)) return parsed;
    } catch {
      // Try next format
    }
  }
  return undefined;
};

/**
 * Normalize a date string to YYYY-MM-DD format.
 * Accepts ISO 8601, yyyy-MM-dd, mm/dd/yyyy, dd/mm/yyyy and other common formats.
 * Use when receiving dates from APIs for components that expect YYYY-MM-DD.
 */
export const toYYYYMMDD = (dateStr?: string): string | undefined => {
  const date = parseFlexibleDate(dateStr);
  if (!date) return undefined;
  return format(date, 'yyyy-MM-dd');
};

/**
 * Format date to YYYY-MM-DD using the UTC calendar day of an instant.
 * Do not use for "today" defaults — use {@link formatIsoLocalDate} instead.
 */
export const formatDateToUTC = (date: Date): string => {
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day = String(date.getUTCDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Format date to YYYY-MM-DD format from local date string
 */
export const formatLocalDateToUTC = (dateString: string): string => {
  const date = new Date(dateString);
  return formatDateToUTC(date);
};

/**
 * Get date range for the previous calendar year (Jan 1 – Dec 31, local).
 */
export const getLastYearRange = () => {
  const lastYear = new Date().getFullYear() - 1;
  const dateFrom = new Date(lastYear, 0, 1);
  const dateTo = new Date(lastYear, 11, 31);

  return {
    dateFrom: formatIsoLocalDate(dateFrom),
    dateTo: formatIsoLocalDate(dateTo),
  };
};

/**
 * Get end date for "Last Year" (December 31 of last year, local calendar).
 */
export const getLastYearEndDate = (): string => {
  const lastYear = new Date().getFullYear() - 1;
  return formatIsoLocalDate(new Date(lastYear, 11, 31));
};

/**
 * Get date range for "This Year" (January 1 through December 31 of current year, local).
 */
export const getThisYearRange = () => {
  const year = new Date().getFullYear();
  const dateFrom = new Date(year, 0, 1);
  const dateTo = new Date(year, 11, 31);

  return {
    dateFrom: formatIsoLocalDate(dateFrom),
    dateTo: formatIsoLocalDate(dateTo),
  };
};

export function isValidIsoLocalDateString(
  value: string | null | undefined,
): value is string {
  if (!value) return false;
  const dateMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!dateMatch) return false;
  const [, yearPart, monthPart, dayPart] = dateMatch;
  const year = Number(yearPart);
  const month = Number(monthPart);
  const day = Number(dayPart);
  if (
    !Number.isFinite(year) ||
    month < 1 ||
    month > 12 ||
    day < 1 ||
    day > 31
  ) {
    return false;
  }
  const parsed = new Date(year, month - 1, day);
  return (
    parsed.getFullYear() === year &&
    parsed.getMonth() === month - 1 &&
    parsed.getDate() === day
  );
}

export function parseIsoLocalDateString(value: string): Date | null {
  if (!isValidIsoLocalDateString(value)) return null;
  const [year, month, day] = value.split('-').map(Number);
  return new Date(year, month - 1, day);
}

/** Shift a local calendar `YYYY-MM-DD` by `days` (negative = earlier). */
export function addIsoLocalDays(isoDate: string, days: number): string | null {
  const parsed = parseIsoLocalDateString(isoDate);
  if (!parsed) return null;
  const next = new Date(parsed);
  next.setDate(next.getDate() + days);
  return formatIsoLocalDate(next);
}

/** Parse a calendar date for display without UTC day shift on `YYYY-MM-DD`. */
export function parseCalendarDisplayDate(value?: string | null): Date | null {
  if (!value?.trim()) return null;
  const trimmed = value.trim();
  if (isValidIsoLocalDateString(trimmed)) {
    return parseIsoLocalDateString(trimmed);
  }
  const parsed = parseFlexibleDate(trimmed);
  return parsed ?? null;
}

/** List tables: abbreviated month (e.g. "Jun 6, 2021"). */
export function formatListDisplayDate(value?: string | null): string {
  const date = parseCalendarDisplayDate(value);
  if (!date) return '';
  return format(date, 'MMM d, yyyy');
}

/** Detail views: full month name (e.g. "June 6, 2021"). */
export function formatDetailDisplayDate(value?: string | null): string {
  const date = parseCalendarDisplayDate(value);
  if (!date) return '';
  return format(date, 'MMMM d, yyyy');
}

/** Compact numeric date (e.g. "06/06/21"). */
export function formatCompactNumericDate(value?: string | null): string {
  const date = parseCalendarDisplayDate(value);
  if (!date) return '';
  return format(date, 'MM/dd/yy');
}

/** General journal month column (e.g. "Apr"). */
export function formatJournalMonthAbbrev(value?: string | null): string {
  const date = parseCalendarDisplayDate(value);
  if (!date) return '';
  return format(date, 'MMM');
}

/** General journal day column, zero-padded (e.g. "01"). */
export function formatJournalDayPadded(value?: string | null): string {
  const date = parseCalendarDisplayDate(value);
  if (!date) return '';
  return format(date, 'dd');
}

/** `YYYY-MM-DD` → month day, year (e.g. "May 31, 2026"). */
export function formatMonthDayYear(isoDate?: string): string {
  return formatDetailDisplayDate(isoDate);
}

/** ISO instant → abbreviated date and time (e.g. "Jun 6, 2021, 4:30pm"). */
export function formatListDisplayDateTime(iso?: string | null): string {
  if (!iso?.trim()) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const datePart = format(date, 'MMM d, yyyy');
  const hours = date.getHours();
  const minutes = date.getMinutes();
  const hour12 = hours % 12 || 12;
  const ampm = hours < 12 ? 'am' : 'pm';
  const timePart =
    minutes === 0
      ? `${hour12}${ampm}`
      : `${hour12}:${String(minutes).padStart(2, '0')}${ampm}`;
  return `${datePart}, ${timePart}`;
}

/** ISO instant → month day year and time (e.g. "June 4 2026, 4pm"). */
export function formatMonthDayYearTime(iso?: string | null): string {
  if (!iso?.trim()) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const datePart = formatDetailDisplayDate(
    `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`,
  );
  const hours = date.getHours();
  const minutes = date.getMinutes();
  const hour12 = hours % 12 || 12;
  const ampm = hours < 12 ? 'am' : 'pm';
  const timePart =
    minutes === 0
      ? `${hour12}${ampm}`
      : `${hour12}:${String(minutes).padStart(2, '0')}${ampm}`;
  return `${datePart}, ${timePart}`;
}

const RESERVE_HH_MM = /^([01]\d|2[0-3]):([0-5]\d)$/;

type ParsedReserveClock = {
  hours: number;
  minutes: number;
  hour12: number;
  ampm: 'am' | 'pm';
  label: string;
};

function parseReserveClockHhMm(hhMm: string): ParsedReserveClock | null {
  const match = RESERVE_HH_MM.exec(hhMm.trim());
  if (!match) return null;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  const hour12 = hours % 12 || 12;
  const ampm = hours < 12 ? 'am' : 'pm';
  return {
    hours,
    minutes,
    hour12,
    ampm,
    label: `${hour12}:${String(minutes).padStart(2, '0')}${ampm}`,
  };
}

/** Venue-local `HH:mm` → 12-hour clock (e.g. `09:00` → `9:00am`). */
export function formatReserveClockTime(hhMm?: string | null): string {
  if (!hhMm?.trim()) return '';
  return parseReserveClockHhMm(hhMm)?.label ?? hhMm.trim();
}

/**
 * Venue-local `HH:mm` range for Reserve UI (e.g. `09:00`–`09:30` → `9:00–9:30am`,
 * `11:30`–`12:30` → `11:30am–12:30pm`).
 */
export function formatReserveClockRange(
  startHhMm?: string | null,
  endHhMm?: string | null,
): string {
  const start = startHhMm?.trim() ? parseReserveClockHhMm(startHhMm) : null;
  const end = endHhMm?.trim() ? parseReserveClockHhMm(endHhMm) : null;
  if (!start || !end) {
    const fallbackStart = startHhMm?.trim() ?? '';
    const fallbackEnd = endHhMm?.trim() ?? '';
    if (!fallbackStart && !fallbackEnd) return '';
    if (!fallbackEnd) return formatReserveClockTime(fallbackStart);
    if (!fallbackStart) return formatReserveClockTime(fallbackEnd);
    return `${fallbackStart}–${fallbackEnd}`;
  }
  if (start.ampm === end.ampm) {
    return `${start.hour12}:${String(start.minutes).padStart(2, '0')}–${end.hour12}:${String(end.minutes).padStart(2, '0')}${end.ampm}`;
  }
  return `${start.label}–${end.label}`;
}

/**
 * Contiguous booking length as hours + minutes (e.g. 90 → `1 hr 30 min`,
 * 60 → `1 hr`, 30 → `30 min`).
 */
export function formatReserveDurationHoursMinutes(
  totalMinutes: number,
): string {
  if (!Number.isFinite(totalMinutes) || totalMinutes <= 0) {
    return '';
  }
  const wholeMinutes = Math.round(totalMinutes);
  const hours = Math.floor(wholeMinutes / 60);
  const minutes = wholeMinutes % 60;
  if (hours > 0 && minutes > 0) {
    return `${hours} hr ${minutes} min`;
  }
  if (hours > 0) {
    return `${hours} hr`;
  }
  return `${minutes} min`;
}

export type RelativeTimeAgoUnit =
  'seconds' | 'minutes' | 'hours' | 'days' | 'weeks' | 'months' | 'years';

export interface RelativeTimeAgoParts {
  count: number;
  unit: RelativeTimeAgoUnit;
}

/** Compact elapsed-time buckets for labels like "25s ago" / "2 days ago". */
export function getRelativeTimeAgoParts(
  iso: string | null | undefined,
  now: Date = new Date(),
): RelativeTimeAgoParts | null {
  if (!iso?.trim()) {
    return null;
  }
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return null;
  }

  let diffSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);
  if (diffSeconds < 0) {
    diffSeconds = 0;
  }

  if (diffSeconds < 60) {
    return { count: diffSeconds, unit: 'seconds' };
  }

  const minutes = Math.floor(diffSeconds / 60);
  if (minutes < 60) {
    return { count: minutes, unit: 'minutes' };
  }

  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    return { count: hours, unit: 'hours' };
  }

  const days = Math.floor(hours / 24);
  if (days < 7) {
    return { count: days, unit: 'days' };
  }

  const weeks = Math.floor(days / 7);
  if (weeks < 5) {
    return { count: weeks, unit: 'weeks' };
  }

  const months = Math.floor(days / 30);
  if (months < 12) {
    return { count: months, unit: 'months' };
  }

  const years = Math.floor(days / 365);
  return { count: Math.max(years, 1), unit: 'years' };
}

/** First and last day of the current local calendar month as `YYYY-MM-DD`. */
export function getLocalCalendarMonthRangeValues(): {
  from: string;
  to: string;
} {
  const now = new Date();
  const from = new Date(now.getFullYear(), now.getMonth(), 1);
  const to = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  return { from: formatIsoLocalDate(from), to: formatIsoLocalDate(to) };
}
